import { ComponentProps, useCallback } from 'react';
import { useNavigate } from 'react-router';
import routes from 'routes';
import { useDelivery } from 'api/hooks/useDelivery';
import { TurnaroundSuccessDialog } from 'components/TurnaroundSuccessDialog/TurnaroundSuccessDialog';
import { useLanguage } from 'context/AppContext';
import { useTurnaroundFormReset } from '../hooks/useTurnaroundFormReset';

type Props = Omit<ComponentProps<typeof TurnaroundSuccessDialog>, 'open' | 'delivery'> & {
  destinationDeliveryId: string | number | null;
  onClose: () => void;
};

export const TurnaroundDestinationSuccessDialog = ({ destinationDeliveryId, onClose, ...props }: Props) => {
  const lang = useLanguage()[0].TurnaroundDestinationSuccessDialog;

  const { data: destinationDelivery, isLoading: destinationDeliveryLoading } = useDelivery(destinationDeliveryId);

  const resetTurnaroundForm = useTurnaroundFormReset();
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    resetTurnaroundForm();
    onClose();
  }, [resetTurnaroundForm, onClose]);

  const onViewDestinationDelivery = useCallback(() => {
    if (!destinationDelivery) {
      throw new Error('Destination delivery not found');
    }

    resetTurnaroundForm();
    handleClose();
    navigate(routes.Surgery.makePath(destinationDelivery?.orderRecordId));
  }, [destinationDelivery, resetTurnaroundForm, handleClose, navigate]);

  const onExit = useCallback(() => {
    resetTurnaroundForm();
    handleClose();
  }, [resetTurnaroundForm, handleClose]);

  return (
    <TurnaroundSuccessDialog
      {...props}
      open
      confirmLabel={lang.viewDestinationDelivery}
      cancelLabel={lang.exit}
      onConfirm={onViewDestinationDelivery}
      onCancel={onExit}
      onClose={handleClose}
      delivery={destinationDelivery}
      loading={destinationDeliveryLoading}
    />
  );
};
